import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  type OnDestroy,
  output,
  signal,
  untracked,
} from '@angular/core';
import { GuidedTourService } from '../../services/guided-tour/guided-tour';
import {
  GUIDED_TOUR_REGISTRY,
  type GuidedTourAnchorRef,
  type GuidedTourRegistry,
  type GuidedTourStep,
} from './guided-tour.model';
import { GuidedTourLayerComponent } from './guided-tour-layer/guided-tour-layer';

interface ActiveGuidedTourStep {
  step: GuidedTourStep;
  target: HTMLElement;
  stepNumber: number;
  stepCount: number;
}

const COMFORTABLE_EDGE_DISTANCE = 120;

/**
 * Walks the user through the `nrTourAnchor` elements below it, the first time they see them. It
 * starts once every required anchor is on screen, and each step dims the rest and frames its
 * anchor. The layer is rendered here and fixed to the window, so inside a dialog it stays within
 * the dialog's focus trap.
 */
@Component({
  selector: 'nr-guided-tour',
  imports: [GuidedTourLayerComponent],
  templateUrl: './guided-tour.html',
  styleUrl: './guided-tour.scss',
  providers: [
    {
      provide: GUIDED_TOUR_REGISTRY,
      useFactory: (): GuidedTourRegistry => inject(GuidedTourComponent),
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GuidedTourComponent implements GuidedTourRegistry, OnDestroy {
  private readonly guidedTour = inject(GuidedTourService);
  private readonly window = inject(DOCUMENT).defaultView;
  private readonly anchors = signal<GuidedTourAnchorRef[]>([]);
  private readonly stepIndex = signal<number | undefined>(undefined);

  readonly tourId = input.required<string>();

  readonly steps = input.required<GuidedTourStep[]>();

  readonly enabled = input<boolean>(true);

  readonly finished = output();

  readonly skipped = output();

  readonly availableSteps = computed(() =>
    this.steps().filter((step) => this.findAnchor(step.anchor)),
  );

  readonly activeStep = computed(() => this.buildActiveStep());

  constructor() {
    effect(() => {
      this.startWhenReady();
    });
    effect(() => {
      const target = this.activeStep()?.target;
      if (target) this.reveal(target);
    });
  }

  ngOnDestroy(): void {
    this.guidedTour.release(this);
  }

  register(anchor: GuidedTourAnchorRef): void {
    untracked(() => {
      this.anchors.update((anchors) => [...anchors, anchor]);
    });
  }

  unregister(anchor: GuidedTourAnchorRef): void {
    untracked(() => {
      this.anchors.update((anchors) => anchors.filter((candidate) => candidate !== anchor));
    });
  }

  next(): void {
    const index = this.stepIndex();
    if (index === undefined) return;
    if (index + 1 >= this.availableSteps().length) {
      this.finish();
      return;
    }
    this.stepIndex.set(index + 1);
  }

  finish(): void {
    if (this.end()) this.finished.emit();
  }

  skip(): void {
    if (this.end()) this.skipped.emit();
  }

  private end(): boolean {
    if (this.stepIndex() === undefined) return false;
    this.stepIndex.set(undefined);
    this.guidedTour.complete(this.tourId(), this);
    return true;
  }

  private startWhenReady(): void {
    const requiredAnchorsReady = this.steps()
      .filter(({ optional }) => !optional)
      .every(({ anchor }) => this.findAnchor(anchor));
    const ready = this.enabled() && this.availableSteps().length > 0 && requiredAnchorsReady;
    if (!ready || untracked(this.stepIndex) !== undefined) return;
    if (!this.guidedTour.claim(this.tourId(), this)) return;
    this.stepIndex.set(0);
  }

  private buildActiveStep(): ActiveGuidedTourStep | undefined {
    const index = this.stepIndex();
    const steps = this.availableSteps();
    const step = index === undefined ? undefined : steps.at(index);
    const target = step ? this.findAnchor(step.anchor) : undefined;
    if (index === undefined || !step || !target) return undefined;
    return { step, target, stepNumber: index + 1, stepCount: steps.length };
  }

  /** The first anchor with that name, as they are registered in document order. */
  private findAnchor(name: string): HTMLElement | undefined {
    return this.anchors().find((anchor) => anchor.name() === name)?.element;
  }

  private reveal(target: HTMLElement): void {
    const { top, bottom } = target.getBoundingClientRect();
    const viewportHeight = this.window?.innerHeight ?? 0;
    const comfortablyVisible =
      top >= COMFORTABLE_EDGE_DISTANCE && bottom <= viewportHeight - COMFORTABLE_EDGE_DISTANCE;
    if (!comfortablyVisible) target.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }
}
